'use client';

import { useEffect, useRef } from 'react';
import { useI18n } from '@fshr/context/i18n-context';
import { StepperFormHeader } from '../stepper-form-header';
import { StepperNav } from '../stepper-nav';
import { StepperFormBody } from '../stepper-form-body';
import { ChangeStepModal } from '../change-step-modal';
import { StepperFormSuccess } from '../stepper-form-success';
import { isReviewStep, type StepperFormProps, type StepSlotProps } from '../types';
import { useStepperForm } from './use-stepper-form';

export type {
	FormStep,
	FormStepChangeReason,
	ReviewStepSlotProps,
	StepperFormProps,
	StepSlotProps,
} from '../types';

export const StepperForm = ({
	title,
	description,
	steps,
	reviewContent,
	reviewNextButtonText,
	extraReviewSections,
	extraReviewSectionsPosition,
	onSubmit,
	analyticsData,
	successTitle,
	successDescription,
	successCtaLabel,
	onSuccessCtaClick,
	titleHTag = 'h1',
	successTitleHTag = 'h2',
}: StepperFormProps) => {
	const { t } = useI18n();
	const {
		activeStep,
		resolvedSteps,
		currentStep: activeStepIndex,
		completedSteps,
		isFirstStep,
		isLastStep,
		isDirty,
		pendingNavigation,
		initialValues,
		showSuccess,
		submitError,
		isSubmitting,
		reviewSections,
		onValuesChange,
		goToStep,
		handleBack,
		handleStepComplete,
		handleModalCancel,
		handleModalDiscard,
		handleModalSave,
	} = useStepperForm({
		steps,
		reviewContent,
		reviewNextButtonText,
		extraReviewSections,
		extraReviewSectionsPosition,
		onSubmit,
		analyticsData,
	});

	const isFirstStepRender = useRef(true);
	const bodyRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (isFirstStepRender.current) {
			isFirstStepRender.current = false;
			return;
		}
		window.scrollTo({ top: 0, behavior: 'auto' });
	}, [activeStepIndex]);

	useEffect(() => {
		if (!isSubmitting) return;
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, [isSubmitting]);

	if (showSuccess) {
		return (
			<StepperFormSuccess
				title={successTitle}
				titleHTag={successTitleHTag}
				description={successDescription}
				ctaLabel={successCtaLabel}
				onCtaClick={onSuccessCtaClick}
			/>
		);
	}

	if (!activeStep) {
		return null;
	}

	const slotProps: StepSlotProps = {
		currentStep: activeStepIndex,
		isFirstStep,
		isLastStep,
		isDirty,
		nextButtonText: activeStep.nextButtonText ?? t('Next'),
		onValuesChange,
		onBack: handleBack,
		onStepComplete: handleStepComplete,
		initialValues,
	};

	const isReview = isReviewStep(activeStep.stepTitle);

	return (
		<div className="flex w-full flex-col spacing-stack-xl">
			<StepperFormHeader
				asHtmlString
				title={title}
				description={description}
				titleHTag={titleHTag}
			/>

			<div className="hidden h-px w-full bg-border-subtle lg:block" aria-hidden />

			<div className="grid w-full grid-cols-1 gap-[30px] lg:grid-cols-12 lg:gap-[30px]">
				<div className="w-full lg:col-span-3">
					<StepperNav
						steps={resolvedSteps}
						currentStep={activeStepIndex}
						completedSteps={completedSteps}
						onStepSelect={goToStep}
					/>
				</div>

				<div className="w-full lg:col-span-9" ref={bodyRef}>
					<StepperFormBody
						stepTitle={activeStep.stepTitle}
						stepContent={activeStep.stepContent}
						stepDescription={activeStep.stepDescription}
						showStepHeader={activeStep.showStepHeader}
						slotProps={slotProps}
						reviewProps={
							isReview
								? {
										sections: reviewSections,
										goToStep,
										submitError,
										isSubmitting,
									}
								: undefined
						}
					/>
				</div>
			</div>

			<ChangeStepModal
				open={pendingNavigation !== null}
				onCancel={handleModalCancel}
				onSave={() => handleModalSave(() => bodyRef.current?.querySelector('form') ?? null)}
				onDiscard={handleModalDiscard}
			/>
		</div>
	);
};
